
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { API } from '../../utils/api';

export function AdminWatches({ headers }) {
  const [watches, setWatches] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ imei: '', model: '', sw_version: '' });

  const fetchWatches = async () => {
    try {
      const res = await axios.get(`${API}/admin/watches`, { headers });
      setWatches(res.data.items || []);
    } catch (e) {
      toast.error('Failed to fetch watches');
    }
  };

  useEffect(() => { 
    fetchWatches(); 
    // Poll for status updates every 10 seconds
    const interval = setInterval(fetchWatches, 10000);
    return () => clearInterval(interval);
  }, []);

  const handleCreate = async () => {
    try {
      await axios.post(`${API}/admin/watches`, form, { headers });
      toast.success('Watch added to stock');
      setShowCreate(false);
      setForm({ imei: '', model: '', sw_version: '' });
      fetchWatches();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add watch');
    }
  };

  const handleReset = async (id) => {
    if (!window.confirm('Reset this watch? This will unbind the user and delete their account.')) return;
    try {
      await axios.post(`${API}/admin/watches/${id}/reset`, {}, { headers });
      toast.success('Watch reset successfully');
      fetchWatches();
    } catch (e) {
      toast.error('Failed to reset watch');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this watch from stock?')) return;
    try {
      await axios.delete(`${API}/admin/watches/${id}`, { headers });
      toast.success('Watch deleted');
      fetchWatches();
    } catch (e) {
      toast.error('Failed to delete watch');
    }
  };

  const toggleSuspend = async (watch) => {
    const newStatus = watch.status === 'SUSPENDED' ? (watch.bound_user_id ? 'BOUND' : 'IN_STOCK') : 'SUSPENDED';
    try {
      await axios.put(`${API}/admin/watches/${watch.id}`, { status: newStatus }, { headers });
      toast.success(`Watch ${newStatus.toLowerCase()}`);
      fetchWatches();
    } catch (e) {
      toast.error('Failed to update status');
    }
  };

  const isOnline = (lastSeen) => {
    if (!lastSeen) return false;
    const diff = new Date() - new Date(lastSeen);
    return diff < 60000; // Online if seen in last 60 seconds
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Watch Stock</h2>
        <Button onClick={() => setShowCreate(true)}>+ Add Device</Button>
      </div>

      {showCreate && (
        <Card>
          <CardHeader><CardTitle>Add New Device</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div><Label>IMEI</Label><Input value={form.imei} onChange={e=>setForm({...form, imei: e.target.value})} /></div>
              <div><Label>Model</Label><Input value={form.model} onChange={e=>setForm({...form, model: e.target.value})} /></div>
              <div><Label>SW Version</Label><Input value={form.sw_version} onChange={e=>setForm({...form, sw_version: e.target.value})} /></div>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleCreate}>Add to Stock</Button>
              <Button variant="secondary" onClick={() => setShowCreate(false)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4">
        {watches.map(w => {
          const online = isOnline(w.last_seen_at);
          return (
            <Card key={w.id}>
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <div className="font-bold text-lg flex items-center gap-2">
                    {w.model}
                    <span className={`flex h-2.5 w-2.5 rounded-full ${online ? 'bg-green-500' : 'bg-gray-300'}`} title={online ? 'Online' : 'Offline'} />
                    {online && <span className="text-xs font-normal text-green-600">Online</span>}
                  </div>
                  <div className="text-sm text-muted-foreground">IMEI: {w.imei} • SW: {w.sw_version || 'N/A'}</div>
                  <div className="flex gap-2 mt-1">
                    <Badge variant={w.status === 'BOUND' ? 'default' : w.status === 'SUSPENDED' ? 'destructive' : 'secondary'}>{w.status}</Badge>
                    {(w.bound_parent_email || w.bound_user_name) && (
                      <span className="text-xs text-blue-600 self-center" data-testid={`watch-bound-to-${w.id}`}>
                        Bound to: {w.bound_parent_email || w.bound_user_name}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => toggleSuspend(w)}>
                    {w.status === 'SUSPENDED' ? 'Activate' : 'Suspend'}
                  </Button>
                  {w.status === 'BOUND' && (
                    <Button variant="secondary" size="sm" onClick={() => handleReset(w.id)}>Reset</Button>
                  )}
                  <Button variant="destructive" size="sm" onClick={() => handleDelete(w.id)}>Delete</Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
        {watches.length === 0 && <div className="text-center text-muted-foreground py-10">No devices in stock</div>}
      </div>
    </div>
  );
}
